import React, { useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import adminApi, { pagingParams } from '../../Api/adminApi';
import PaginatedTable from '../../MyComponents/PaginatedTable/PaginatedTable';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import { lessonColumns } from './lessonsColumns';
import { toDateParam } from './adminFormat';

function defaultFrom() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
}

/**
 * Lesson activity: one row per teacher session with the class, how many
 * students joined, when it started and how long it ran. Filtered by date range
 * and a text search over class name or teacher email.
 */
const LessonsTab = () => {
    const classes = useAdminStyles();
    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(new Date());
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('starttime');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebounced(search.trim());
            setPage(0);
        }, 400);
        return () => clearTimeout(handle);
    }, [search]);

    const query = {
        ...pagingParams({ rowsPerPage, page, orderBy, order }),
        from: from && !isNaN(from.getTime()) ? toDateParam(from) : '',
        to: to && !isNaN(to.getTime()) ? toDateParam(to) : '',
        search: debounced,
    };
    const { data, isLoading, isError, isForbidden } = useAdminQuery(() => adminApi.getLessons(query), [
        JSON.stringify(query),
    ]);

    const setOrderFlags = (nextOrder, nextOrderBy) => {
        setOrder(nextOrder);
        setOrderBy(nextOrderBy);
        setPage(0);
    };

    if (isForbidden || (isError && !data)) {
        return <StateCard isError={isError} isForbidden={isForbidden} />;
    }

    return (
        <>
            <p className={classes.muted} style={{ margin: '0 0 14px', fontSize: '0.9rem' }}>
                One row per teacher session. Students counts the distinct participants who were in the class while the
                teacher was connected.
            </p>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <div className={classes.filters}>
                    <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        inputVariant="outlined"
                        size="small"
                        format="dd/MM/yyyy"
                        label="From"
                        value={from}
                        onChange={(d) => {
                            setFrom(d);
                            setPage(0);
                        }}
                        KeyboardButtonProps={{ 'aria-label': 'change from date' }}
                        className={classes.filterControl}
                    />
                    <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        inputVariant="outlined"
                        size="small"
                        format="dd/MM/yyyy"
                        label="To"
                        value={to}
                        onChange={(d) => {
                            setTo(d);
                            setPage(0);
                        }}
                        KeyboardButtonProps={{ 'aria-label': 'change to date' }}
                        className={classes.filterControl}
                    />
                    <TextField
                        className={classes.filterSearch}
                        variant="outlined"
                        size="small"
                        label="Search class or teacher email"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </MuiPickersUtilsProvider>
            <PaginatedTable
                title={`Lessons (${data?.count || 0})`}
                headCells={lessonColumns}
                rows={data?.lessons || []}
                rowCount={data?.count || 0}
                order={order}
                orderBy={orderBy}
                setOrder={setOrderFlags}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={(n) => {
                    setRowsPerPage(n);
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                isLoading={isLoading}
                emptyMessage="No lessons in this period."
            />
        </>
    );
};

export default LessonsTab;
